import { Button, TextField, Box, Grid } from '@mui/material'
import React, { Fragment, useEffect, useRef, useState } from 'react'
import { CameraScanButton } from '../../_WMS/CameraScan'
import { useScanKeyboard } from '../../../hooks/useScanKeyboard'
import {
  PickingInputData,
  PickingRouteDetails,
  StockKeepingUnit
} from '../../../interfaces/Picking/Picking'
import { intIfNumber } from '../helpers'
import { numberStyles } from '../styles'

interface PickingProductFormProps {
  pickingRouteDetails?: PickingRouteDetails
  initialInputData: PickingInputData
  isInputQuantity: boolean
  handleFormSubmit: (inputData: PickingInputData) => void
  handlerOpenAndCloseDialogAlert?: (
    showSeconds: number,
    message: string
  ) => void | undefined
  statePositionsScaned?: string[] | undefined
  setStatePositionsScaned?:
    | React.Dispatch<React.SetStateAction<string[]>>
    | undefined
  configPickingRoutePositionScan: boolean
}

export const PickingProductForm: React.FC<PickingProductFormProps> = ({
  pickingRouteDetails,
  initialInputData,
  isInputQuantity,
  handleFormSubmit,
  handlerOpenAndCloseDialogAlert,
  statePositionsScaned,
  setStatePositionsScaned,
  configPickingRoutePositionScan
}) => {
  const positionInputRef = useRef<HTMLInputElement>(null)
  const skuInputRef = useRef<HTMLInputElement>(null)

  // En modo camara el teclado virtual no se levanta salvo que toquen el input.
  const { autoFocus } = useScanKeyboard()
  const details: PickingRouteDetails =
    pickingRouteDetails || new PickingRouteDetails()
  const product: StockKeepingUnit =
    pickingRouteDetails?.product || new StockKeepingUnit()
  const pendingQuantity = details.realQuantity - details.pickedQuantity
  const initialInput: PickingInputData = {
    sku: '',
    quantity: pendingQuantity,
    positionName: ''
  }

  const [inputData, setInputData] = useState<PickingInputData>(initialInputData)
  const [formValues, setValues] = useState(initialInput)
  const [statePositionName, setStatePositionName] = useState(false) // Si la posición ya fue escaneada deja el control disabled y se auto completa.
  const [pendingCameraSubmit, setPendingCameraSubmit] = useState(false)
  const [formErrors, setErrors] = useState({} as any)
  const setFieldError = (name: string, value: any, errors: any) => {
    if (name === 'sku') {
      delete errors.sku
      if (value === '') {
        errors.sku = 'Campo requerido'
      } else if (value !== product.sku && value !== product.ean13) {
        errors.sku = 'SKU no corresponde'
      }
    }

    if (name === 'quantity' && isInputQuantity) {
      delete errors.quantity
      const qty = parseInt(value)
      if (!value) {
        errors.quantity = 'Campo requerido'
      } else if (qty < 1) {
        errors.quantity = 'Mínimo una unidad requerida'
      } else if (qty > pendingQuantity) {
        errors.quantity = 'Excede cantidad solicitada'
      }
    }

    if (configPickingRoutePositionScan && name === 'positionName') {
      delete errors.position
      if (value === '') {
        errors.position = 'Campo requerido'
      } else if (value !== pickingRouteDetails?.position?.positionName) {
        if (handlerOpenAndCloseDialogAlert !== undefined) {
          errors.position = 'Posición no corresponde'
        }
      }
    }
  }

  const validateInput = (name: string, value: any) => {
    const newErrors: any = { ...formErrors }
    setFieldError(name, value, newErrors)
    setErrors({ ...newErrors })
  }

  const handleQtyDec = () => {
    if (inputData.quantity > 1) {
      const qty = inputData.quantity - 1
      setInputData({ ...inputData, quantity: qty })
      setValues({ ...formValues, quantity: qty })
      validateInput('quantity', '' + qty)
    }
  }

  const handleQtyInc = () => {
    if (inputData.quantity < details.realQuantity) {
      const qty = inputData.quantity + 1
      setInputData({ ...inputData, quantity: qty })
      setValues({ ...formValues, quantity: qty })
      validateInput('quantity', '' + qty)
    }
  }

  const handleInputValue = (e: any) => {
    const name = e.target.name
    const value = intIfNumber(e, ['quantity'])
    setValues({ ...formValues, [name]: value })
    validateInput(name, value)
    setInputData({ ...inputData, [name]: value })
  }

  const handleInputValuePosition = (e: any) => {
    if (configPickingRoutePositionScan) {
      const name = e.target.name
      const value = intIfNumber(e, ['position'])
      setValues({ ...formValues, [name]: value })
      validateInput(name, value)
      setInputData({ ...inputData, [name]: value })
    }
  }

  const formIsValid = () => {
    const errors: any = {}
    Object.keys(formValues).forEach((key) => {
      setFieldError(key, formValues[key], errors)
    })
    setErrors({ ...errors })
    const isValid = Object.values(errors).every((x) => x === '')
    return isValid
  }

  /**
   * Un solo boton de camara para posicion y producto.
   *
   * Va a posicion mientras el escaneo de posicion este configurado, no venga ya
   * escaneada de otro detalle de la ruta, y siga vacia. En cualquier otro caso
   * va al producto.
   */
  const isScanningPosition =
    configPickingRoutePositionScan &&
    !statePositionName &&
    !inputData.positionName.trim()

  const handleCameraScan = (value: string) => {
    const scanned = value.trim()
    if (!scanned) return
    // Union literal y no string: PickingInputData no tiene index signature.
    const name: 'positionName' | 'sku' = isScanningPosition ? 'positionName' : 'sku'
    setValues({ ...formValues, [name]: scanned })
    validateInput(name, scanned)
    setInputData({ ...inputData, [name]: scanned })
    /**
     * El submit se difiere un render en vez de llamarse aca mismo: handleSubmit
     * usa formIsValid(), que lee formValues del estado, y en este tick todavia
     * tiene el valor anterior.
     *
     * Diferirlo reproduce exactamente lo que ocurre con el lector fisico:
     * onChange actualiza el estado, React re-renderiza, y recien despues llega
     * el Enter. Asi se reusa toda la validacion y los dialogos de error que ya
     * existen, incluido el que mueve el foco al SKU cuando la posicion es
     * correcta pero falta el producto.
     */
    setPendingCameraSubmit(true)
  }

  const handleCameraCancel = () => {
    if (!autoFocus) return
    setTimeout(() => {
      if (isScanningPosition) positionInputRef.current?.focus()
      else skuInputRef.current?.focus()
    }, 0)
  }

  const handleSubmit = (e: any) => {
    const isValid = formIsValid()
    if (isValid) {
      handleFormSubmit(inputData)

      // validación solo para scanear posición
      if (configPickingRoutePositionScan) {
        if (setStatePositionsScaned !== undefined) {
          const lastedPositionScaned = statePositionsScaned?.filter(
            (f) => f === pickingRouteDetails?.position?.positionName
          )[0]
          if (!lastedPositionScaned) {
            setStatePositionsScaned((prevPositions) => [
              ...prevPositions,
              inputData.positionName
            ])
          }
        }
      }
    } else {
      if (inputData.positionName.trim() !== '' && formErrors.position) {
        if (handlerOpenAndCloseDialogAlert !== undefined) {
          handlerOpenAndCloseDialogAlert(
            3, // segundos antes de auto cerrar el dialogo
            `Ubicación “${inputData.positionName}” no coincide con la solicitada`
          )
          setInputData({ ...inputData, positionName: '' })
        }
      } else {
        handleMoveFocusSku() // corrección: En caso que la posición sea erronea no debe mandar el foco al sku. Debe seguir en la posición
      }

      if (inputData.sku.trim() !== '' && formErrors.sku) {
        if (handlerOpenAndCloseDialogAlert !== undefined) {
          handlerOpenAndCloseDialogAlert(
            3, // segundos antes de auto cerrar el dialogo
            `El Sku “${inputData.sku}” no corresponde con el solicitado`
          )
        }

        setInputData({ ...inputData, sku: '' })
      }
    }
    e.preventDefault()
  }

  /**
   * Dispara el submit del escaneo por camara recien despues del render, cuando
   * formValues y formErrors ya tienen el valor leido.
   */
  useEffect(() => {
    if (!pendingCameraSubmit) return
    setPendingCameraSubmit(false)
    handleSubmit({ preventDefault: () => {} })
  }, [pendingCameraSubmit])

  useEffect(() => {
    // validar la posición
    const lastedPositionScaned = statePositionsScaned?.filter(
      (f) => f === pickingRouteDetails?.position?.positionName
    )[0]

    if (lastedPositionScaned) {
      const name: string = 'positionName'
      setValues({ ...formValues, [name]: lastedPositionScaned })
      setInputData({ ...inputData, [name]: lastedPositionScaned })
      setStatePositionName(true)
      handleMoveFocusSku()
    } else {
      setStatePositionName(false)
    }
  }, [])

  const handleMoveFocusSku = () => {
    // En modo camara no se enfoca el SKU al validar la posicion: abriria el
    // teclado virtual justo cuando el operario va a escanear el producto.
    if (!autoFocus) return
    if (skuInputRef && skuInputRef.current) {
      skuInputRef.current.focus()
    }
  }

  return (
    <Fragment>
      <Grid
        container
        spacing={2}
        direction='column'
        alignItems='center'
        justifyContent='center'
      >
        <Grid
          container
          item
          xs={12}
          justifyContent='center'
          alignItems='center'
        >
          <Box component='form' display='flex' alignItems='center' p={1} m={1}>
            <Grid container spacing={3}>
              {configPickingRoutePositionScan && (
                <Grid item xs={12} sm={6}>
                  <Box display='flex' alignItems='center'>
                    <TextField
                      id='positionName'
                      name='positionName'
                      label={'Escanear Posición'}
                      onChange={handleInputValuePosition}
                      value={inputData.positionName}
                      autoFocus={autoFocus}
                      autoComplete='off'
                      {...(configPickingRoutePositionScan &&
                        formErrors.position && {
                          error: true,
                          helperText: formErrors.position
                        })}
                      disabled={statePositionName}
                      inputRef={positionInputRef}
                    />
                  </Box>
                </Grid>
              )}
              <Grid item xs={12} sm={configPickingRoutePositionScan ? 6 : 12}>
                <Box display='flex' alignItems='center'>
                  <TextField
                    id='sku'
                    name='sku'
                    label={'Escanear producto'}
                    onChange={handleInputValue}
                    value={inputData.sku}
                    autoComplete='off'
                    {...(formErrors.sku && {
                      error: true,
                      helperText: formErrors.sku
                    })}
                    inputRef={skuInputRef}
                    autoFocus={autoFocus && !configPickingRoutePositionScan} // el auto foco se activa solo si la configuración de "escaneo de posición" esta inactiva.
                  />
                  <Button
                    type='submit'
                    onClick={handleSubmit}
                    variant='contained'
                    color='primary'
                    style={{ marginLeft: 10 }}
                  >
                    {'>'}
                  </Button>
                </Box>
              </Grid>

              {/* Un solo boton para los dos campos: el titulo indica cual se lee. */}
              <Grid item xs={12}>
                <CameraScanButton
                  inputRef={isScanningPosition ? positionInputRef : skuInputRef}
                  onScan={handleCameraScan}
                  onCancel={handleCameraCancel}
                  dialogTitle={isScanningPosition ? 'Escanear posición' : 'Escanear producto'}
                  helperText={
                    isScanningPosition
                      ? 'Centra el código de la posición dentro del recuadro'
                      : 'Centra el código del producto dentro del recuadro'
                  }
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>

      {isInputQuantity && (
        <Box alignItems='center' marginTop='15px' marginBottom='15px'>
          <Grid
            container
            spacing={2}
            direction='row'
            alignItems='center'
            justifyContent='space-around'
          >
            <Grid item xs={3}>
              <Button
                size='small'
                variant='contained'
                color='primary'
                onClick={handleQtyDec}
              >
                -
              </Button>
            </Grid>
            <Grid item xs={6}>
              <TextField
                id='quantity'
                name='quantity'
                size='small'
                autoComplete='off'
                type='number'
                onBlur={handleInputValue}
                onChange={handleInputValue}
                value={inputData.quantity}
                {...(formErrors.quantity && {
                  error: true,
                  helperText: formErrors.quantity
                })}
                style={numberStyles}
              />
            </Grid>
            <Grid item xs={3}>
              <Button
                variant='contained'
                color='primary'
                onClick={handleQtyInc}
                size='small'
              >
                +
              </Button>
            </Grid>
          </Grid>
        </Box>
      )}
    </Fragment>
  )
}
